import { apiRequest } from './client'
import type { OrganizationDto } from './types'
import type { UpdateOrganizationRequest } from './organizations'
import { readInt } from './json'
import { normalizeRfq, type RfqRaw } from './rfqWire'

export type OperatorSupplierSummaryDto = {
  id: number
  legal_name: string
  ogrn: string | null
  inn: string | null
  city: string | null
  trade_name: string | null
  match_suggested: number
  match_unmatched: number
  needs_attention: number
  active: boolean
}

export type OperatorRestaurantSummaryDto = {
  id: number
  legal_name: string
  ogrn: string | null
  inn: string | null
  city: string | null
  trade_name: string | null
  rfq_count: number
  active: boolean
}

type PageDto<T> = {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export type OperatorSuppliersPageDto = PageDto<OperatorSupplierSummaryDto>
export type OperatorRestaurantsPageDto = PageDto<OperatorRestaurantSummaryDto>

export type OperatorUpdateRfqRequest = {
  title?: string
  description?: string
  status?: string
  desiredDeliveryDate?: string | null
  desiredDeliveryTimeUntil?: string | null
  deliveryAddress?: string | null
}

export type OrganizationPlan = 'start' | 'standard' | 'premium'

export const ORGANIZATION_PLAN_OPTIONS: { value: OrganizationPlan; label: string }[] = [
  { value: 'start', label: 'Старт' },
  { value: 'standard', label: 'Стандарт' },
  { value: 'premium', label: 'Премиум' },
]

export type OperatorBuyerOwnerDto = {
  user_profile_id: number | null
  display_name: string | null
  email: string | null
  phone: string | null
  job_title: string | null
  member_role: string | null
  registered_at: string | null
}

export const normalizeOperatorSupplier = (json: Record<string, unknown>): OperatorSupplierSummaryDto => ({
  id: readInt(json, 'id', 'id'),
  legal_name: String(json.legal_name ?? json.legalName ?? ''),
  ogrn: (json.ogrn as string | null) ?? null,
    inn: (json.inn as string | null) ?? null,
  city: (json.city as string | null) ?? null,
  trade_name: (json.trade_name as string | null) ?? (json.tradeName as string | null) ?? null,
  match_suggested: readInt(json, 'matchSuggested', 'match_suggested'),
  match_unmatched: readInt(json, 'matchUnmatched', 'match_unmatched'),
  needs_attention: readInt(json, 'needsAttention', 'needs_attention'),
  active: json.active !== false,
})

export const normalizeOperatorRestaurant = (json: Record<string, unknown>): OperatorRestaurantSummaryDto => ({
  id: readInt(json, 'id', 'id'),
  legal_name: String(json.legal_name ?? json.legalName ?? ''),
  ogrn: (json.ogrn as string | null) ?? null,
    inn: (json.inn as string | null) ?? null,
  city: (json.city as string | null) ?? null,
  trade_name: (json.trade_name as string | null) ?? (json.tradeName as string | null) ?? null,
  rfq_count: readInt(json, 'rfqCount', 'rfq_count'),
  active: json.active !== false,
})

const listPaged = async <T>(
  path: string,
  page: number,
  size: number,
  search: string | undefined,
  normalize: (json: Record<string, unknown>) => T,
): Promise<PageDto<T>> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) })
  if (search?.trim()) {
    params.set('search', search.trim())
  }
  const raw = await apiRequest<{
    content: Record<string, unknown>[]
    page: number
    size: number
    totalElements?: number
    total_elements?: number
    totalPages?: number
    total_pages?: number
  }>(`${path}?${params.toString()}`)
  return {
    page: raw.page,
    size: raw.size,
    // Конверт Spring Page тоже переименуется при переходе бэкенда на snake_case:
    // totalElements → total_elements. Остальные постраничные экраны уже терпимы.
    totalElements: Number(raw.totalElements ?? raw.total_elements ?? 0),
    totalPages: Number(raw.totalPages ?? raw.total_pages ?? 0),
    content: raw.content.map(normalize),
  }
}

export const listOperatorSuppliers = (page = 0, size = 50, search?: string) =>
  listPaged('/operator/suppliers', page, size, search, normalizeOperatorSupplier)

export const listOperatorRestaurants = (page = 0, size = 50, search?: string) =>
  listPaged('/operator/buyers', page, size, search, normalizeOperatorRestaurant)

export const getOperatorSupplier = (organizationId: number) =>
  apiRequest<OrganizationDto>(`/operator/suppliers/${organizationId}`)

export const updateOperatorSupplier = (organizationId: number, request: UpdateOrganizationRequest) =>
  apiRequest<OrganizationDto>(`/operator/suppliers/${organizationId}`, {
    method: 'PATCH',
    body: {
      legal_name: request.legalName,
      trade_name: request.tradeName,
      ogrn: request.ogrn,
      city: request.city,
      horeca_type: request.horecaType ?? undefined,
      product_type_ids: request.productTypeIds ?? undefined,
    },
  })

export const setOperatorSupplierActive = (organizationId: number, active: boolean) =>
  apiRequest<OrganizationDto>(`/operator/suppliers/${organizationId}/active`, {
    method: 'PATCH',
    body: { active },
  })

export const getOperatorRestaurant = (organizationId: number) =>
  apiRequest<OrganizationDto>(`/operator/buyers/${organizationId}`)

export const updateOperatorRestaurant = (organizationId: number, request: UpdateOrganizationRequest) =>
  apiRequest<OrganizationDto>(`/operator/buyers/${organizationId}`, {
    method: 'PATCH',
    body: {
      legal_name: request.legalName,
      trade_name: request.tradeName,
      ogrn: request.ogrn,
      city: request.city,
      horeca_type: request.horecaType ?? undefined,
    },
  })

export const setOperatorRestaurantActive = (organizationId: number, active: boolean) =>
  apiRequest<OrganizationDto>(`/operator/buyers/${organizationId}/active`, {
    method: 'PATCH',
    body: { active },
  })

// Ответы оператора приходят в snake_case: без разбора экран показывал «#undefined» вместо
// поставщика, пустую дату и ссылку «К списку» на /operator/buyers/undefined/rfqs.
export const listOperatorRestaurantRfqs = async (organizationId: number) =>
  (await apiRequest<RfqRaw[]>(`/operator/buyers/${organizationId}/rfqs`)).map(normalizeRfq)

export const setOperatorSupplierPlan = (organizationId: number, plan: OrganizationPlan) =>
  apiRequest<OrganizationDto>(`/operator/suppliers/${organizationId}/plan`, {
    method: 'PATCH',
    body: { plan },
  })

export const setOperatorRestaurantPlan = (organizationId: number, plan: OrganizationPlan) =>
  apiRequest<OrganizationDto>(`/operator/buyers/${organizationId}/plan`, {
    method: 'PATCH',
    body: { plan },
  })

export const setOperatorRestaurantVenueSlots = (organizationId: number, paidVenueSlots: number) =>
  apiRequest<OrganizationDto>(`/operator/buyers/${organizationId}/venue-slots`, {
    method: 'PATCH',
    body: { paid_venue_slots: paidVenueSlots },
  })

export const getOperatorRestaurantOwner = (organizationId: number) =>
  apiRequest<OperatorBuyerOwnerDto>(`/operator/buyers/${organizationId}/owner`)

export const getOperatorSupplierOwner = (organizationId: number) =>
  apiRequest<OperatorBuyerOwnerDto>(`/operator/suppliers/${organizationId}/owner`)

export const getOperatorRfq = async (rfqId: number) =>
  normalizeRfq(await apiRequest<RfqRaw>(`/operator/rfqs/${rfqId}`))

export const updateOperatorRfq = async (rfqId: number, request: OperatorUpdateRfqRequest) =>
  normalizeRfq(await apiRequest<RfqRaw>(`/operator/rfqs/${rfqId}`, {
    method: 'PATCH',
    body: {
      title: request.title,
      description: request.description,
      status: request.status,
      desired_delivery_date: request.desiredDeliveryDate ?? undefined,
      desired_delivery_time_until: request.desiredDeliveryTimeUntil ?? undefined,
      delivery_address: request.deliveryAddress ?? undefined,
    },
  }))
